import {
  Box,
  Card,
  CardContent,
  Grid,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { grey } from '@mui/material/colors';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { ReactElement, ReactNode, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import { CardHeaderFlex } from '../../component/cardHeaderFlex/CardHeaderFlex';
import { CopyTsvButton } from '../../component/copyTsvButton/CopyTsvButton';
import { FleetMap } from '../../component/vehicle/FleetMap';
import { FleetVehicles } from '../../component/vehicle/FleetVehicles';
import { useMarketTerm } from '../../hooks/useMarketTerm';
import { fetchActivitySummary, getActivitySummaryFetching, getActivitySummaryFor } from '../../redux/slices/activity/activitySlice';
import { getEquipmentSummary, itemEquipmentSummary, itemEquipmentSummaryFetching } from '../../redux/slices/tools/toolsSlice';
import { useDispatch } from '../../redux/store';
import { CardStyled } from '../../styled/components';
import ActivityBarChart, { ActivityBar } from '../tools/activity/ActivityBarChart';
import { EQUIPMENT_SERIES } from '../tools/activity/activityColors';
import { metricsOf, periodLabel } from '../tools/activity/activityWindow';
import { NOT_TRACKED_STATUSES, itemsLink } from '../tools/itemsLink';
import { viewerTimeZone } from './kpiWindow';

type View = 'map' | 'metrics';

// One rule for every percent cell: whole numbers.
const formatPct = (value: number): string => `${Math.round(value)}%`;

// Same type and row rhythm as the shared Table component (14px, 600 heads,
// tinted head and totals rows) so the panel reads like every other table on
// the page. Numbers are tabular and right-aligned under right-aligned heads.
// Plain white rows with a hairline between them; the heads are quiet
// secondary text and only the group bands and the Total row carry a tint.
const cellSx = { py: 1.25, px: 1, fontSize: 14, whiteSpace: 'nowrap', borderBottom: 1, borderBottomColor: 'divider', bgcolor: 'transparent' };
const numSx = { ...cellSx, fontVariantNumeric: 'tabular-nums' };
const headSx = { ...cellSx, py: 0.75, fontWeight: 400, color: 'text.secondary' };
// Group bands sit over each Cnt/% pair as a light rounded pill; a spacer
// column separates groups instead of a vertical rule.
const groupSx = {
  ...cellSx,
  py: 0.5,
  fontWeight: 400,
  color: 'text.secondary',
  fontSize: 13,
  bgcolor: grey[100],
  borderBottom: 'none',
  borderRadius: '6px',
};
const spacerSx = { p: 0, width: 10, borderBottom: 'none', bgcolor: 'transparent' };
const totalSx = { ...numSx, fontWeight: 600, bgcolor: grey[100], borderTop: 2, borderTopColor: 'divider', borderBottom: 'none' };
// The Total band runs unbroken across the spacer columns.
const totalSpacerSx = { ...spacerSx, bgcolor: grey[100], borderTop: 2, borderTopColor: 'divider' };

const CountLink = ({ to, children }: { to: string; children: string }): ReactElement => (
  <Link component={RouterLink} to={to} underline="hover" sx={{ '&&, &&:visited, &&:hover': { color: 'text.primary' } }}>
    {children}
  </Link>
);

// A count: muted when zero so sparse rows stop competing for attention.
const Count = ({ value, to }: { value: number; to?: string }): ReactElement => {
  const text = value.toLocaleString();
  if (!value) {
    return (
      <Box component="span" sx={{ color: 'text.disabled' }}>
        {text}
      </Box>
    );
  }
  return to ? <CountLink to={to}>{text}</CountLink> : <>{text}</>;
};

// A percent with a thin bar beneath it showing the share.
const Pct = ({ value }: { value: number }): ReactElement => (
  <Box component="span" sx={{ position: 'relative', display: 'block', color: value ? undefined : 'text.disabled' }}>
    {formatPct(value)}
    <Box component="span" sx={{ position: 'absolute', left: 0, right: 0, bottom: -5, height: 2, overflow: 'hidden', borderRadius: 1 }}>
      <Box
        component="span"
        sx={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: `${Math.min(100, Math.max(0, value))}%`, bgcolor: 'primary.light' }}
      />
    </Box>
  </Box>
);

type RemediationEquipmentPanelProps = { vehiclesHeaderAddon?: ReactNode };

// The Remediation home page's version of the two-card row every dashboard has.
// A toggle in the left card's corner swaps the pair: the vehicle table with the
// fleet map, or equipment on hand and in the field by product category with
// warehouse activity by day, week or month.
export const RemediationEquipmentPanel = ({ vehiclesHeaderAddon }: RemediationEquipmentPanelProps): ReactElement => {
  const dispatch = useDispatch();
  const term = useMarketTerm();
  const [view, setView] = useState<View>('metrics');
  const summary = useSelector(getEquipmentSummary);
  const summaryFetching = useSelector(itemEquipmentSummaryFetching);
  // The last seven days, fixed. The Activity tab is where a longer window and
  // the trend line live; here the question is just "what moved this week".
  const activity = useSelector(getActivitySummaryFor('week', 'trailing'));
  const activityFetching = useSelector(getActivitySummaryFetching);
  const tz = useMemo(() => viewerTimeZone(), []);
  const theme = useTheme();
  const isSmUp = useMediaQuery(theme.breakpoints.up('sm'));

  useEffect(() => {
    dispatch(itemEquipmentSummary());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchActivitySummary({ period: 'week', mode: 'trailing', tz }));
  }, [dispatch, tz]);

  const movement = useMemo<ActivityBar[]>(
    () =>
      (activity?.periods || []).map((row) => {
        const m = metricsOf(row);
        return {
          key: row.date || '',
          label: periodLabel(row, 'day', tz),
          title: periodLabel(row, 'day', tz),
          values: [m.equipment_out, m.equipment_in],
          notes: [
            { name: 'Jobs opened', value: String(m.jobs_opened) },
            { name: 'Jobs closed', value: String(m.jobs_closed) },
            { name: 'Returns / Cancellations', value: `${m.boomerang_real} / ${m.boomerang_artifact}` },
          ],
        };
      }),
    [activity, tz],
  );

  const onSite = term('Tracked');
  const atHome = term('Not Tracked');

  const toggle = (
    <ToggleButtonGroup size="small" exclusive value={view} onChange={(_event, value: View | null) => value && setView(value)}>
      <ToggleButton value="map">Map</ToggleButton>
      <ToggleButton value="metrics">Metrics</ToggleButton>
    </ToggleButtonGroup>
  );

  if (view === 'map') {
    return (
      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <FleetVehicles
            title="Vehicles & Tool Rooms"
            headerAddon={
              <Stack direction="row" alignItems="center" spacing={1}>
                {toggle}
                {vehiclesHeaderAddon}
              </Stack>
            }
          />
        </Grid>
        {isSmUp && (
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <FleetMap withTrafficFullScreen fullScreenControl={false} />
            </Card>
          </Grid>
        )}
      </Grid>
    );
  }

  return (
    <Grid container spacing={4}>
      <Grid item xs={12} md={6}>
        <CardStyled sx={{ height: '100%' }}>
          <CardHeaderFlex
            title="Equipment Status"
            legend={
              <Stack direction="row" alignItems="center" spacing={1}>
                {toggle}
                <CopyTsvButton
                  label="Equipment table"
                  header={['Product Category', 'Total', `${onSite} Cnt`, `${onSite} %`, 'Onsite-Ready Cnt', 'Onsite-Ready %', `${atHome} Cnt`, `${atHome} %`]}
                  rows={[
                    ...summary.categories.map((row) => [
                      row.category,
                      row.total,
                      row.onJob,
                      formatPct(row.onJobPct),
                      row.pickup,
                      formatPct(row.pickupPct),
                      row.notTracked,
                      formatPct(row.notTrackedPct),
                    ]),
                    ...(summary.categories.length
                      ? [
                          [
                            'Total',
                            summary.total,
                            summary.onJob,
                            formatPct(summary.onJobPct),
                            summary.pickup,
                            formatPct(summary.pickupPct),
                            summary.notTracked,
                            formatPct(summary.notTrackedPct),
                          ],
                        ]
                      : []),
                  ]}
                />
              </Stack>
            }
          />
          <CardContent sx={{ pt: 0, '&:last-child': { pb: 1.5 } }}>
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...headSx, borderBottom: 'none' }} colSpan={2} />
                    <TableCell sx={spacerSx} />
                    <TableCell sx={groupSx} align="right" colSpan={2}>
                      {onSite}
                    </TableCell>
                    <TableCell sx={spacerSx} />
                    <TableCell sx={groupSx} align="right" colSpan={2}>
                      Onsite-Ready
                    </TableCell>
                    <TableCell sx={spacerSx} />
                    <TableCell sx={groupSx} align="right" colSpan={2}>
                      {atHome}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={headSx}>Product Category</TableCell>
                    <TableCell sx={headSx} align="right">
                      Total
                    </TableCell>
                    <TableCell sx={spacerSx} />
                    <TableCell sx={headSx} align="right">
                      Cnt
                    </TableCell>
                    <TableCell sx={headSx} align="right">
                      %
                    </TableCell>
                    <TableCell sx={spacerSx} />
                    <TableCell sx={headSx} align="right">
                      Cnt
                    </TableCell>
                    <TableCell sx={headSx} align="right">
                      %
                    </TableCell>
                    <TableCell sx={spacerSx} />
                    <TableCell sx={headSx} align="right">
                      Cnt
                    </TableCell>
                    <TableCell sx={headSx} align="right">
                      %
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!summaryFetching && !summary.categories.length && (
                    <TableRow>
                      <TableCell sx={cellSx} colSpan={11}>
                        No equipment found.
                      </TableCell>
                    </TableRow>
                  )}
                  {summary.categories.map((row) => (
                    <TableRow key={row.category} sx={{ '&:hover td': { bgcolor: grey[50] } }}>
                      <TableCell sx={cellSx}>{row.category}</TableCell>
                      <TableCell sx={numSx} align="right">
                        <Count value={row.total} to={itemsLink({ category: row.category })} />
                      </TableCell>
                      <TableCell sx={spacerSx} />
                      <TableCell sx={numSx} align="right">
                        <Count value={row.onJob} />
                      </TableCell>
                      <TableCell sx={numSx} align="right">
                        <Pct value={row.onJobPct} />
                      </TableCell>
                      <TableCell sx={spacerSx} />
                      <TableCell sx={numSx} align="right">
                        <Count value={row.pickup} />
                      </TableCell>
                      <TableCell sx={numSx} align="right">
                        <Pct value={row.pickupPct} />
                      </TableCell>
                      <TableCell sx={spacerSx} />
                      <TableCell sx={numSx} align="right">
                        <Count value={row.notTracked} to={itemsLink({ category: row.category, scanStatus: NOT_TRACKED_STATUSES })} />
                      </TableCell>
                      <TableCell sx={numSx} align="right">
                        <Pct value={row.notTrackedPct} />
                      </TableCell>
                    </TableRow>
                  ))}
                  {!!summary.categories.length && (
                    <TableRow>
                      <TableCell sx={{ ...totalSx, fontVariantNumeric: 'normal', borderRadius: '6px 0 0 6px' }}>Total</TableCell>
                      <TableCell sx={totalSx} align="right">
                        <Count value={summary.total} to={itemsLink({})} />
                      </TableCell>
                      <TableCell sx={totalSpacerSx} />
                      <TableCell sx={totalSx} align="right">
                        <Count value={summary.onJob} />
                      </TableCell>
                      <TableCell sx={totalSx} align="right">
                        {formatPct(summary.onJobPct)}
                      </TableCell>
                      <TableCell sx={totalSpacerSx} />
                      <TableCell sx={totalSx} align="right">
                        <Count value={summary.pickup} />
                      </TableCell>
                      <TableCell sx={totalSx} align="right">
                        {formatPct(summary.pickupPct)}
                      </TableCell>
                      <TableCell sx={totalSpacerSx} />
                      <TableCell sx={totalSx} align="right">
                        <Count value={summary.notTracked} to={itemsLink({ scanStatus: NOT_TRACKED_STATUSES })} />
                      </TableCell>
                      <TableCell sx={{ ...totalSx, borderRadius: '0 6px 6px 0' }} align="right">
                        {formatPct(summary.notTrackedPct)}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
            {summaryFetching && (
              <Typography variant="caption" color="text.secondary">
                Loading…
              </Typography>
            )}
          </CardContent>
        </CardStyled>
      </Grid>
      <Grid item xs={12} md={6}>
        <CardStyled sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <CardHeaderFlex
            title="Equipment Movement"
            legend={
              <Typography variant="caption" color="text.secondary">
                Last 7 days
              </Typography>
            }
          />
          {/* The plot grows into whatever height the Equipment table set for
              the row, so the axis labels finish on the same line as its last
              row rather than a few pixels below it. */}
          <CardContent sx={{ pt: 0, flexGrow: 1, display: 'flex', flexDirection: 'column', '&:last-child': { pb: 1.5 } }}>
            <ActivityBarChart
              fill
              bars={movement}
              series={EQUIPMENT_SERIES}
              fetching={activityFetching}
              // A floor, not a height: low enough that the Equipment table
              // decides how tall the row is, so the plot fills to its baseline.
              height={170}
              formatValue={(value) => Math.round(value).toLocaleString()}
            />
          </CardContent>
        </CardStyled>
      </Grid>
    </Grid>
  );
};
